import { useState, useCallback } from 'react';
import {
  Card,
  Tabs,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Banner,
  List,
  Box,
} from '@shopify/polaris';
import type { SummaryResponse } from '../types';
import { formatRelativeOrDate } from '../utils/dateFormat';
import { LlmUsageIndicator } from './LlmUsageIndicator';

/** Tab identifiers matching summary formats. */
const TAB_IDS = ['short', 'long', 'comparison'] as const;

interface SummaryDisplayProps {
  summary: SummaryResponse;
  onRegenerate?: () => void;
  regenerating?: boolean;
}

/**
 * T089 [US3]: Displays AI-generated summaries in a 3-tab layout.
 * Tabs: Short · Long · Comparison. Copy-to-clipboard + stale indicator.
 */
export function SummaryDisplay({ summary, onRegenerate, regenerating }: SummaryDisplayProps) {
  const [selectedTab, setSelectedTab] = useState(0);

  const tabs = [
    { id: 'short', content: 'Short', accessibilityLabel: 'Short summary' },
    { id: 'long', content: 'Long', accessibilityLabel: 'Long description' },
    { id: 'comparison', content: 'Comparison', accessibilityLabel: 'Comparison list' },
  ];

  const handleCopy = useCallback(async () => {
    const activeTab = TAB_IDS[selectedTab];
    let text = '';

    if (activeTab === 'short') text = summary.shortSummary ?? '';
    else if (activeTab === 'long') text = summary.longDescription ?? '';
    else if (activeTab === 'comparison') text = (summary.comparisonList ?? []).join('\n');

    if (text) await navigator.clipboard.writeText(text);
  }, [selectedTab, summary]);

  const hasContent =
    summary.shortSummary || summary.longDescription || summary.comparisonList?.length;

  if (!hasContent) {
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center">
            <LlmUsageIndicator />
            <Text as="h3" variant="headingSm">AI Summaries</Text>
          </InlineStack>
          <Text as="p" tone="subdued">No summaries generated yet.</Text>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <InlineStack gap="200" align="start" blockAlign="center">
              <LlmUsageIndicator />
              <Text as="h3" variant="headingSm">AI Summaries</Text>
            </InlineStack>
            {summary.isStale && <Badge tone="warning">Stale</Badge>}
          </InlineStack>
          <InlineStack gap="200" blockAlign="center">
            <Button size="slim" onClick={handleCopy}>Copy</Button>
            {onRegenerate && (
              <Button
                size="slim"
                variant="primary"
                onClick={onRegenerate}
                loading={regenerating}
              >
                Regenerate
              </Button>
            )}
          </InlineStack>
        </InlineStack>

        {summary.isStale && (
          <Banner tone="info">
            Summaries are slightly out of date and will be automatically refreshed later today.
          </Banner>
        )}

        <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
          <Box paddingBlockStart="300">
            {selectedTab === 0 && <ShortTab text={summary.shortSummary} />}
            {selectedTab === 1 && <LongTab text={summary.longDescription} />}
            {selectedTab === 2 && <ComparisonTab items={summary.comparisonList} />}
          </Box>
        </Tabs>

        {summary.generatedAt && (
          <Text as="p" variant="bodySm" tone="subdued">
            Generated {formatRelativeOrDate(summary.generatedAt)}
          </Text>
        )}

        <Banner tone="info">
          <Text as="p" variant="bodySm">
            AI summaries make your products discoverable by AI-powered shopping assistants
            like ChatGPT, Perplexity, and Google AI Overviews. They are delivered via your
            storefront JSON-LD and product feed.
          </Text>
        </Banner>
      </BlockStack>
    </Card>
  );
}

function ShortTab({ text }: { text: string | null }) {
  if (!text) return <Text as="p" tone="subdued">Not generated</Text>;
  return (
    <BlockStack gap="200">
      <Text as="p">{text}</Text>
      <Text as="p" variant="bodySm" tone="subdued">{text.length} characters</Text>
    </BlockStack>
  );
}

function LongTab({ text }: { text: string | null }) {
  if (!text) return <Text as="p" tone="subdued">Not generated</Text>;
  return (
    <BlockStack gap="200">
      <Text as="p">{text}</Text>
      <Text as="p" variant="bodySm" tone="subdued">{text.length} characters</Text>
    </BlockStack>
  );
}

/**
 * Normalise a stored comparison list — handles two broken formats from older
 * LLM responses that didn't use newlines correctly:
 *   1. Single-element array where all items are comma-and-dash joined inline.
 *   2. Items that still carry a leading "- " bullet marker.
 */
function normaliseComparisonItems(items: string[]): string[] {
  const expanded: string[] = [];
  for (const item of items) {
    // If an item itself contains ",- " it was never split properly — split it now
    if (/,\s*-\s+/.test(item)) {
      expanded.push(...item.split(/,\s*-\s+/).map((s) => s.trim()).filter(Boolean));
    } else if (item.includes('\n')) {
      expanded.push(...item.split('\n').map((s) => s.trim()).filter(Boolean));
    } else {
      expanded.push(item.trim());
    }
  }
  // Strip any remaining leading "- " bullet markers
  return expanded.map((item) => item.replace(/^-\s+/, '').trim()).filter(Boolean);
}

function ComparisonTab({ items }: { items: string[] | null }) {
  if (!items?.length) return <Text as="p" tone="subdued">Not generated</Text>;
  const cleaned = normaliseComparisonItems(items);
  return (
    <List type="bullet">
      {cleaned.map((item, i) => (
        <List.Item key={i}>{item}</List.Item>
      ))}
    </List>
  );
}
